import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from 'swiper/react';
import { motion } from "framer-motion";
import 'swiper/css';
import 'swiper/css/navigation';
import { Autoplay, Navigation } from 'swiper/modules';
import ContactSection from "../components/ContactSection";

const Career = () => {

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const [activeRole, setActiveRole] = useState(null);

    const values = [
        {
            icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="#2563eb" strokeWidth="1.8" strokeLinejoin="round" /></svg>
            ),
            title: 'Integrity',
            desc: 'We uphold transparency, accountability, and ethical conduct in every engagement - with clients, partners, and each other.'
        },
        {
            icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="#2563eb" strokeWidth="1.8" /><path d="M12 8v4l3 3" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" /></svg>
            ),
            title: 'Ownership',
            desc: 'We take responsibility for outcomes. Every team member is empowered to lead, deliver, and improve continuously.'
        },
        {
            icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M9.663 17h4.673M12 3v1m6.364 1.636-.707.707M21 12h-1M4 12H3m3.343-5.657-.707-.707M6 20l6-7 6 7" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
            ),
            title: 'Innovation',
            desc: 'We encourage forward-thinking solutions, creative problem-solving, and a mindset of continuous improvement and technology leadership.'
        },
        {
            icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" /><circle cx="9" cy="7" r="4" stroke="#2563eb" strokeWidth="1.8" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" /></svg>
            ),
            title: 'Collaboration',
            desc: 'We believe collective intelligence drives stronger results. Teamwork is at the core of everything we do and every outcome we deliver.'
        }
    ];

    const benefits = [
        {
            id: '01',
            title: 'Structured Learning & Certifications',
            desc: 'We invest in continuous learning through technical training, certification support, and dedicated career development pathways to help every employee stay future-ready.'
        },
        {
            id: '02',
            title: 'Statutory Benefits & Compliance',
            desc: 'We ensure full statutory compliance including Provident Fund, ESIC, and Gratuity - providing complete security and regulatory assurance for every employee.'
        },
        {
            id: '03',
            title: 'Personal & Family Insurance',
            desc: 'Comprehensive health insurance coverage for employees and their families, ensuring financial and medical peace of mind when it matters most.'
        },
        {
            id: '04',
            title: 'Five-Day Work Week',
            desc: 'A structured five-day working model that promotes productivity while enabling employees to maintain a healthy, sustainable work-life balance.'
        },
        {
            id: '05',
            title: 'Wellness Programmes',
            desc: 'Regular wellness initiatives, team activities, and engagement programmes that support physical, mental, and emotional wellbeing across the organisation.'
        },
        {
            id: '06',
            title: 'Career Growth',
            desc: 'Clear growth pathways, leadership mentoring, and performance-linked advancement opportunities that fuel long-term professional development and progression.'
        }
    ];

    const testimonials = [
        {
            name: 'Urmila Sati',
            role: 'Help Desk Manager',
            avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&auto=format&fit=crop&q=80',
            quote: 'Joining Solaris has been one of the best decisions of my career. Over time it has become more than just an organisation - it has been a place where I have grown both personally and professionally. The collaborative culture, the leadership support, and the opportunities to grow under leaders who truly invest in every person have shaped who I am today.'
        },
        {
            name: 'Anwesha Chakraborty',
            role: 'Marketing Manager',
            avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&auto=format&fit=crop&q=80',
            quote: 'My journey at Solaris has been incredibly fulfilling. I am encouraged to think creatively, take initiative, and make a real impact. The genuine support from leadership and the continuous opportunities to learn, grow, and contribute with both skill and personality have made this an environment where I truly thrive.'
        },
        {
            name: 'Ravi Shankar',
            role: 'Network Engineer',
            avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&auto=format&fit=crop&q=80',
            quote: 'Solaris gives engineers the freedom to work on meaningful, complex infrastructure challenges with proper tools and mentorship. The team culture is collaborative, the leadership is accessible, and there are genuine pathways to develop technical expertise and move into senior roles.'
        }
    ];

    const openRoles = [
        { title: 'Marketing Intern', location: 'Mumbai', arrangement: 'On-site', posted: '03 May 2026' },
        { title: 'Sr. Client Manager – Mumbai (BFS)', location: 'Mumbai', arrangement: 'On-site', posted: '03 May 2026' },
        { title: 'Sr. Client Manager – Gurugram', location: 'Gurugram', arrangement: 'On-site', posted: '03 May 2026' },
        { title: 'Sr. Client Manager – Bangalore', location: 'Bangalore', arrangement: 'On-site', posted: '03 May 2026' },
        { title: 'Pre-Sales Consultant – All Locations', location: 'Multiple Locations', arrangement: 'On-site', posted: '03 May 2026' },
        { title: 'Client Manager – Mumbai', location: 'Mumbai', arrangement: 'On-site', posted: '03 May 2026' },
        { title: 'Client Manager – Hyderabad', location: 'Hyderabad', arrangement: 'On-site', posted: '03 May 2026' },
        { title: 'Client Manager – Bangalore', location: 'Bangalore', arrangement: 'On-site', posted: '03 May 2026' },
    ];

    const teamPhotos = [
        'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=600&auto=format&fit=crop&q=80',
        'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=600&auto=format&fit=crop&q=80',
        'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&auto=format&fit=crop&q=80',
    ];

    return (
        <>
            {/* ── HERO ── */}
            <section style={{
                position: 'relative', width: '100%', minHeight: '520px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundImage: `url('https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1600&auto=format&fit=crop&q=80')`,
                backgroundSize: 'cover', backgroundPosition: 'center', overflow: 'hidden'
            }}>
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(8,16,40,0.76)' }} />
                <div style={{
                    position: 'absolute', inset: 0,
                    backgroundImage: 'linear-gradient(rgba(37,99,235,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(37,99,235,0.06) 1px, transparent 1px)',
                    backgroundSize: '60px 60px', pointerEvents: 'none'
                }} />
                <div className="container" style={{ position: 'relative', zIndex: 2, padding: '140px 15px 100px', textAlign: 'center' }}>
                    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                        <div style={{
                            display: 'inline-flex', alignItems: 'center', gap: '8px',
                            background: 'rgba(37,99,235,0.2)', border: '1px solid rgba(37,99,235,0.4)',
                            borderRadius: '50px', padding: '6px 18px', marginBottom: '28px'
                        }}>
                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#60a5fa' }} />
                            <span style={{ color: '#60a5fa', fontWeight: 700, letterSpacing: '2.5px', textTransform: 'uppercase', fontSize: '0.72rem' }}>
                                Join Our Team
                            </span>
                        </div>
                        <h1 style={{
                            color: '#fff', fontWeight: 800,
                            fontSize: 'clamp(2.2rem, 5vw, 4rem)',
                            lineHeight: 1.15, margin: '0 auto 24px', maxWidth: '820px'
                        }}>
                            Let's shape the future<br />
                            <span style={{
                                background: 'linear-gradient(135deg, #60a5fa, #38bdf8)',
                                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
                            }}>of Tech, together</span>
                        </h1>
                        <p style={{
                            color: 'rgba(255,255,255,0.72)', fontSize: '1.05rem',
                            lineHeight: 1.8, maxWidth: '540px', margin: '0 auto 40px'
                        }}>
                            At Solaris, we build more than infrastructure - we build careers. Join a team of engineers, strategists, and innovators who are passionate about making technology work better for businesses.
                        </p>
                        <a href="#open-roles" style={{ textDecoration: 'none' }}>
                            <button style={{
                                display: 'inline-flex', alignItems: 'center',
                                background: 'linear-gradient(135deg,#2563eb,#0ea5e9)',
                                color: '#fff', border: 'none',
                                padding: '14px 8px 14px 28px',
                                borderRadius: '50px', fontWeight: 700, fontSize: '1rem',
                                cursor: 'pointer', boxShadow: '0 8px 24px rgba(37,99,235,0.4)',
                                transition: 'transform 0.2s, box-shadow 0.2s'
                            }}
                                onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(37,99,235,0.55)'; }}
                                onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(37,99,235,0.4)'; }}
                            >
                                <span style={{ marginRight: '16px' }}>View Open Roles</span>
                                <span style={{
                                    background: '#fff', borderRadius: '50%', width: '38px', height: '38px',
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                        <path d="M7 17L17 7M17 7H7M17 7V17" stroke="#0f172a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </span>
                            </button>
                        </a>
                    </motion.div>
                </div>
            </section>

            {/* ── TEAM PHOTO STRIP ── */}
            <section style={{ padding: '72px 0', background: '#fff' }}>
                <div className="container">
                    <div className="row g-3">
                        {teamPhotos.map((src, i) => (
                            <div key={i} className="col-md-4">
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: i * 0.1 }}
                                    viewport={{ once: true }}
                                    style={{ borderRadius: '16px', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}
                                >
                                    <img src={src} alt="Solaris team" style={{ width: '100%', height: '240px', objectFit: 'cover', display: 'block' }} />
                                </motion.div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── VALUES ── */}
            <section style={{ padding: '90px 0', background: '#f8fafc' }}>
                <div className="container">
                    <div style={{ marginBottom: '56px' }}>
                        <p style={{
                            color: '#2563eb', fontWeight: 700, letterSpacing: '3px',
                            textTransform: 'uppercase', fontSize: '0.78rem', marginBottom: '14px'
                        }}>OUR CORE VALUES</p>
                        <h2 style={{ fontSize: 'clamp(1.9rem, 3.5vw, 2.6rem)', fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                            The Values That Guide Us
                        </h2>
                    </div>
                    <div className="row g-4">
                        {values.map((v, i) => (
                            <div key={i} className="col-lg-3 col-md-6">
                                <motion.div
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: i * 0.1 }}
                                    viewport={{ once: true }}
                                    whileHover={{ y: -6, boxShadow: '0 16px 40px rgba(37,99,235,0.1)', borderColor: '#bfdbfe' }}
                                    style={{
                                        padding: '32px 24px', borderRadius: '16px',
                                        border: '1px solid #e2e8f0', background: '#fff',
                                        height: '100%', cursor: 'default', transition: 'all 0.3s'
                                    }}
                                >
                                    <div style={{
                                        width: '48px', height: '48px', borderRadius: '12px',
                                        background: '#eff6ff', display: 'flex',
                                        alignItems: 'center', justifyContent: 'center', marginBottom: '20px'
                                    }}>
                                        {v.icon}
                                    </div>
                                    <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a', marginBottom: '10px' }}>{v.title}</h3>
                                    <p style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: 1.75, marginBottom: 0 }}>{v.desc}</p>
                                </motion.div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── BENEFITS ── */}
            <section style={{ padding: '90px 0', background: '#fff' }}>
                <div className="container">
                    <div style={{ marginBottom: '56px' }}>
                        <p style={{
                            color: '#2563eb', fontWeight: 700, letterSpacing: '3px',
                            textTransform: 'uppercase', fontSize: '0.78rem', marginBottom: '14px'
                        }}>BENEFITS</p>
                        <h2 style={{ fontSize: 'clamp(1.9rem, 3.5vw, 2.6rem)', fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                            Why Build Your Career at Solaris
                        </h2>
                    </div>
                    <div className="row g-4">
                        {benefits.map((b, i) => (
                            <div key={i} className="col-lg-4 col-md-6">
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: i * 0.08 }}
                                    viewport={{ once: true }}
                                    style={{
                                        padding: '32px 28px', borderRadius: '16px',
                                        border: '1px solid #e2e8f0', background: '#fff',
                                        height: '100%', cursor: 'default'
                                    }}
                                    whileHover={{ y: -5, boxShadow: '0 16px 40px rgba(37,99,235,0.08)', borderColor: '#bfdbfe' }}
                                >
                                    <div style={{
                                        fontSize: '0.72rem', fontWeight: 700, color: '#2563eb',
                                        letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '14px'
                                    }}>+ {b.id}</div>
                                    <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a', marginBottom: '12px', lineHeight: 1.4 }}>{b.title}</h3>
                                    <p style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: 1.75, marginBottom: 0 }}>{b.desc}</p>
                                </motion.div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── TESTIMONIALS ── */}
            <section style={{ padding: '90px 0', background: '#f8fafc' }}>
                <div className="container">
                    <div style={{ textAlign: 'center', marginBottom: '60px' }}>
                        <p style={{
                            color: '#2563eb', fontWeight: 700, letterSpacing: '3px',
                            textTransform: 'uppercase', fontSize: '0.78rem', marginBottom: '14px'
                        }}>TESTIMONIALS</p>
                        <h2 style={{ fontSize: 'clamp(1.9rem, 3.5vw, 2.6rem)', fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                            What Our Employees{' '}
                            <span style={{
                                background: 'linear-gradient(135deg, #2563eb, #0ea5e9)',
                                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
                            }}>Say About Us</span>
                        </h2>
                    </div>

                    <div style={{ position: 'relative' }}>
                        <Swiper
                            modules={[Navigation, Autoplay]}
                            spaceBetween={24}
                            slidesPerView={1}
                            navigation={{ prevEl: '.career-prev-btn', nextEl: '.career-next-btn' }}
                            autoplay={{ delay: 5000, disableOnInteraction: false }}
                            breakpoints={{ 768: { slidesPerView: 2 }, 1200: { slidesPerView: 2 } }}
                            className="career-swiper pb-4"
                        >
                            {testimonials.map((t, i) => (
                                <SwiperSlide key={i}>
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.5 }}
                                        viewport={{ once: true }}
                                        style={{
                                            background: '#fff', borderRadius: '20px',
                                            padding: '40px 36px', border: '1px solid #e2e8f0',
                                            boxShadow: '0 4px 24px rgba(0,0,0,0.05)', height: '100%'
                                        }}
                                    >
                                        <div style={{ marginBottom: '24px' }}>
                                            {[...Array(5)].map((_, s) => (
                                                <svg key={s} width="16" height="16" viewBox="0 0 24 24" fill="#f59e0b" style={{ marginRight: '3px' }}>
                                                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                                                </svg>
                                            ))}
                                        </div>
                                        <p style={{
                                            color: '#475569', fontSize: '0.95rem', lineHeight: 1.85,
                                            marginBottom: '28px', fontStyle: 'italic'
                                        }}>
                                            "{t.quote}"
                                        </p>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                                            <img
                                                src={t.avatar}
                                                alt={t.name}
                                                style={{ width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #dbeafe' }}
                                            />
                                            <div>
                                                <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.95rem' }}>{t.name}</div>
                                                <div style={{ color: '#64748b', fontSize: '0.82rem' }}>{t.role}</div>
                                            </div>
                                        </div>
                                    </motion.div>
                                </SwiperSlide>
                            ))}
                        </Swiper>

                        <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '28px' }}>
                            <button className="career-prev-btn" style={{
                                width: '40px', height: '40px', borderRadius: '50%',
                                border: '1.5px solid #cbd5e1', background: '#fff', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: '#475569', transition: 'all 0.2s'
                            }}
                                onMouseOver={e => { e.currentTarget.style.background = '#2563eb'; e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.color = '#fff'; }}
                                onMouseOut={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.color = '#475569'; }}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M15 19L8 12L15 5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </button>
                            <button className="career-next-btn" style={{
                                width: '40px', height: '40px', borderRadius: '50%',
                                border: '1.5px solid #2563eb', background: '#2563eb', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: '#fff', transition: 'all 0.2s'
                            }}
                                onMouseOver={e => { e.currentTarget.style.background = '#1d4ed8'; e.currentTarget.style.borderColor = '#1d4ed8'; }}
                                onMouseOut={e => { e.currentTarget.style.background = '#2563eb'; e.currentTarget.style.borderColor = '#2563eb'; }}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── OPEN ROLES ── */}
            <section id="open-roles" style={{ padding: '90px 0', background: '#fff' }}>
                <div className="container">
                    <div style={{ textAlign: 'center', marginBottom: '56px' }}>
                        <p style={{
                            color: '#2563eb', fontWeight: 700, letterSpacing: '3px',
                            textTransform: 'uppercase', fontSize: '0.78rem', marginBottom: '14px'
                        }}>OPEN ROLES</p>
                        <h2 style={{ fontSize: 'clamp(1.9rem, 3.5vw, 2.6rem)', fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                            Explore Current Opportunities
                        </h2>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {openRoles.map((role, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: i * 0.06 }}
                                viewport={{ once: true }}
                                style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    padding: '24px 28px', borderRadius: '14px',
                                    border: '1px solid #e2e8f0', background: '#fff',
                                    transition: 'box-shadow 0.2s, border-color 0.2s',
                                    flexWrap: 'wrap', gap: '16px'
                                }}
                                whileHover={{ boxShadow: '0 8px 32px rgba(37,99,235,0.1)', borderColor: '#bfdbfe' }}
                            >
                                <div style={{ flex: 1, minWidth: '200px' }}>
                                    <h4 style={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a', marginBottom: '8px' }}>{role.title}</h4>
                                    <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#64748b', fontSize: '0.82rem' }}>
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="#94a3b8" strokeWidth="1.8" /><circle cx="12" cy="10" r="3" stroke="#94a3b8" strokeWidth="1.8" /></svg>
                                            {role.location}
                                        </span>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#64748b', fontSize: '0.82rem' }}>
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><rect x="2" y="7" width="20" height="14" rx="2" stroke="#94a3b8" strokeWidth="1.8" /><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" stroke="#94a3b8" strokeWidth="1.8" /></svg>
                                            {role.arrangement}
                                        </span>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#94a3b8', fontSize: '0.82rem' }}>
                                            Posted on {role.posted}
                                        </span>
                                    </div>
                                </div>
                                <Link to="/contact" style={{ textDecoration: 'none' }}>
                                    <button style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '0',
                                        background: 'linear-gradient(135deg,#2563eb,#0ea5e9)',
                                        color: '#fff', border: 'none',
                                        padding: '10px 6px 10px 20px',
                                        borderRadius: '50px', fontWeight: 700, fontSize: '0.85rem',
                                        cursor: 'pointer', whiteSpace: 'nowrap',
                                        boxShadow: '0 4px 14px rgba(37,99,235,0.3)',
                                        transition: 'transform 0.2s, box-shadow 0.2s'
                                    }}
                                        onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(37,99,235,0.45)'; }}
                                        onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(37,99,235,0.3)'; }}
                                    >
                                        <span style={{ marginRight: '12px' }}>Apply Now</span>
                                        <span style={{
                                            background: '#fff', borderRadius: '50%',
                                            width: '30px', height: '30px',
                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                        }}>
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                                                <path d="M7 17L17 7M17 7H7M17 7V17" stroke="#2563eb" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </span>
                                    </button>
                                </Link>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            <ContactSection />
        </>
    );
};

export default Career;
